<template>
  <v-system-bar v-if="shouldShowBanner" height="40" window :color="color" variant="tonal">
    <v-btn
      v-if="currentAlert ? currentAlert.dismissible : dismissible"
      variant="text"
      density="compact"
      @click="onDismiss"
      icon="mdi-close-circle"
      class="ml-3"
    />
    <v-icon :icon="currentAlert ? currentAlert.icon : icon" class="ml-6 mr-2" />
    <span class="font-weight-bold text-body-2">{{
      currentAlert ? currentAlert.title : title
    }}</span>
    <v-spacer />
    <span v-html="currentAlert ? currentAlert.message : message" class="text-body-2"></span>
    <v-spacer />

    <v-btn
      v-if="currentAlert ? currentAlert.allowDontShowAgain : allowDontShowAgain"
      variant="text"
      @click="dontShowAgain"
      class="ml-2"
    >
      Don't show again
    </v-btn>
    <v-btn
      v-if="currentAlert ? currentAlert.actionText : actionText"
      variant="outlined"
      @click="onAction"
      class="ml-2"
    >
      {{ currentAlert ? currentAlert.actionText : actionText }}
    </v-btn>
  </v-system-bar>
</template>

<script>
import { getCurrentAlert, handleAlertAction } from '@/config/alerts'

export default {
  name: 'GlobalAlertBanner',
  props: {
    // Props below are only used if not using the alerts.js system
    alertId: {
      type: String,
      default: ''
    },
    show: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    color: {
      type: String,
      default: 'info'
    },
    icon: {
      type: String,
      default: 'mdi-information'
    },
    actionText: {
      type: String,
      default: ''
    },
    dismissible: {
      type: Boolean,
      default: false
    },
    allowDontShowAgain: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      localStorageKey: 'syft-hidden-alerts',
      currentAlert: null,
      isVisible: true
    }
  },
  computed: {
    shouldShowBanner() {
      // If manually hidden in this session
      if (!this.isVisible) return false

      // If using the alerts.js system
      if (this.currentAlert) {
        // Check if this alert has been dismissed with "don't show again"
        const hiddenAlerts = this.getHiddenAlerts()
        return !hiddenAlerts.includes(this.currentAlert.id)
      }

      // If using the props system
      if (this.alertId) {
        if (!this.show) return false

        // Check if this alert has been dismissed with "don't show again"
        const hiddenAlerts = this.getHiddenAlerts()
        return !hiddenAlerts.includes(this.alertId)
      }

      return false
    },

    // Get the actual alert ID to use
    activeAlertId() {
      return this.currentAlert ? this.currentAlert.id : this.alertId
    }
  },
  methods: {
    onAction() {
      if (this.currentAlert) {
        handleAlertAction(this.currentAlert)
      }
    },

    onDismiss() {
      this.isVisible = false
    },

    dontShowAgain() {
      const hiddenAlerts = this.getHiddenAlerts()
      const alertId = this.activeAlertId

      // Add current alertId to the list if not already present
      if (alertId && !hiddenAlerts.includes(alertId)) {
        hiddenAlerts.push(alertId)
        localStorage.setItem(this.localStorageKey, JSON.stringify(hiddenAlerts))
      }

      this.isVisible = false
    },

    getHiddenAlerts() {
      try {
        const stored = localStorage.getItem(this.localStorageKey)
        return stored ? JSON.parse(stored) : []
      } catch (error) {
        console.error('Error parsing hidden alerts from localStorage:', error)
        return []
      }
    },

    // Utility method that can be called to reset all hidden alerts
    resetAllHiddenAlerts() {
      localStorage.removeItem(this.localStorageKey)
    }
  },
  mounted() {
    // Load the current alert from the config
    this.currentAlert = getCurrentAlert()
  }
}
</script>
